// @givmo/ai — the assistant SSE event payloads (contract
// `assistant-ui-blocks.schema.json` §1.2, `turn_events.py`). These are the `data:`
// JSON payloads carried by each typed SSE event in a turn stream. Mirrors iOS
// `AssistantSSEEvent.swift`.
//
// Canonical successful-turn order:
//   message_start → tool_status* → text_delta* → message_complete
// Terminal set: `message_complete | turn_failed | turn_cancelled` — the SSE reader
// stops tailing on any terminal.
import type { KnownUiBlock } from './blocks';

/** Tool-status state (`ToolStatusState`, §1.2). Open-tolerant. */
export type ToolStatusState = 'started' | 'done' | (string & {});

/** `message_start` payload (`turn_events.py:70`). */
export interface MessageStartPayload {
  turn_id: string;
  message_id: string;
  conversation_id: string;
  attempt: number;
}

/** `tool_status` payload (`turn_events.py:55`). `label` is a server-default
 * friendly label clients may localize/override. */
export interface ToolStatusPayload {
  tool: string;
  label: string;
  state: ToolStatusState;
}

/** `text_delta` payload (`turn_events.py:90`) — one incremental text chunk. */
export interface TextDeltaPayload {
  delta: string;
}

/** Token usage on a completed turn. The schema types `usage` as an open object;
 * the fields the client reads are the token counts, optional so an additive future
 * usage field never breaks decoding. */
export interface AssistantUsage {
  input_tokens?: number;
  output_tokens?: number;
  [key: string]: unknown;
}

/** `message_complete` payload (`turn_events.py:100` + additive `ui_blocks` §2.1).
 * The terminal event that finalizes the assistant message; `ui_blocks` rides here
 * (additive optional — a payload from before ui_blocks shipped omits it).
 * `suggested_replies` is currently always `[]`. */
export interface MessageCompletePayload {
  message_id: string;
  stop_reason: string | null;
  usage: AssistantUsage;
  suggested_replies: string[];
  /** Additive (§2.1). The blocks ride on the wire as raw ui_blocks; the client
   * runs `decodeUiBlocks` over them to apply the §2.3 forward-compat rule. */
  ui_blocks?: KnownUiBlock[];
}

/** `turn_failed` payload (`turn_events.py:121`). */
export interface TurnFailedPayload {
  code: string;
  user_message: string;
  retryable: boolean;
}

/** `turn_cancelled` payload (`turn_events.py:139`). `message_id` is null when
 * nothing was generated before the cancel. */
export interface TurnCancelledPayload {
  message_id: string | null;
}

/** The 6 SSE event type discriminators (§1.2). */
export const SSE_EVENT_TYPES = [
  'message_start',
  'tool_status',
  'text_delta',
  'message_complete',
  'turn_failed',
  'turn_cancelled',
] as const;
export type SseEventType = (typeof SSE_EVENT_TYPES)[number];

/** The terminal event set (`TERMINAL_EVENT_TYPES`, `turn_events.py:35`). */
export const TERMINAL_EVENT_TYPES = ['message_complete', 'turn_failed', 'turn_cancelled'] as const;
export type TerminalEventType = (typeof TERMINAL_EVENT_TYPES)[number];

export function isTerminalEventType(type: string): type is TerminalEventType {
  return (TERMINAL_EVENT_TYPES as readonly string[]).includes(type);
}

/**
 * A typed SSE event a turn stream yields — the event name plus its decoded
 * payload. `unknown`-typed events (a future `event:` name) surface as
 * `{ type: 'unknown', name, payload }` so the consumer can skip them without the
 * client crashing (§3.7 crash-safety: never rethrow a future event/malformed
 * payload).
 */
export type AssistantStreamEvent =
  | { type: 'message_start'; payload: MessageStartPayload }
  | { type: 'tool_status'; payload: ToolStatusPayload }
  | { type: 'text_delta'; payload: TextDeltaPayload }
  | { type: 'message_complete'; payload: MessageCompletePayload }
  | { type: 'turn_failed'; payload: TurnFailedPayload }
  | { type: 'turn_cancelled'; payload: TurnCancelledPayload }
  | { type: 'unknown'; name: string; payload: unknown };

/** The `202 SendMessageAccepted` transport DTO (`assistant_entities.py:964`). The
 * client opens `stream_path` for the SSE stream. */
export interface SendMessageAccepted {
  turn_id: string;
  status: string;
  stream_path: string;
}
